/**
 * Modal de configuração para node de Set Variable
 * Permite definir qual variável receberá qual valor
 * 
 * RELEASE: 1.1.0 - 2025-12-28 - Adicionado suporte a Data Dinâmica
 * RELEASE: 1.2.0 - 2026-09-06 - Variável de Destino passa a usar o
 *           VariableExplorerModal em modo 'destination' (somente variáveis
 *           graváveis do cliente), substituindo o combobox simples
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FlowNode, SetVariableConfig, DynamicDateConfig } from '@/types/flows';
import { z } from 'zod';
import { toast } from 'sonner';
import { HelpCircle, Calendar, Info, ChevronsUpDown, X } from 'lucide-react';
import { NodeHelpDialog } from './NodeHelpDialog';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';
import { VariableExplorerModal } from '../variables/VariableExplorerModal';
import { useAuth } from '@/hooks/useAuth';
import { previewDynamicDate } from '@/lib/dynamic-date';

// Schema de validação
const setVariableSchema = z.object({
  variable_id: z.string().min(1, 'Selecione uma variável'),
  variable_name: z.string().min(1, 'Nome da variável é obrigatório'),
  value_type: z.enum(['static', 'variable', 'expression', 'dynamic_date']),
  value: z.string(),
});

interface ConfigureSetVariableDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode | null;
  onSave: (nodeId: string, config: SetVariableConfig) => void;
}

/**
 * Dialog de configuração de Set Variable
 */
export function ConfigureSetVariableDialog({ 
  open, 
  onOpenChange, 
  node, 
  onSave 
}: ConfigureSetVariableDialogProps) {
  const { currentTenant } = useAuth();
  const nicknameRef = useRef<NodeIdFieldRef>(null);
  const [variableId, setVariableId] = useState(node?.data.config?.variable_id || '');
  const [variableName, setVariableName] = useState(node?.data.config?.variable_name || '');
  const [valueType, setValueType] = useState<'static' | 'variable' | 'expression' | 'dynamic_date'>(
    node?.data.config?.value_type || 'static'
  );
  const [value, setValue] = useState(node?.data.config?.value || '');
  const [showHelp, setShowHelp] = useState(false);
  // Controla abertura do modal de seleção da variável de destino
  const [showVarPicker, setShowVarPicker] = useState(false);
  
  // Estados para configuração de data dinâmica
  const [dateBase, setDateBase] = useState<DynamicDateConfig['base']>(
    node?.data.config?.date_config?.base || 'today'
  );
  const [dateOffsetType, setDateOffsetType] = useState<DynamicDateConfig['offset_type']>(
    node?.data.config?.date_config?.offset_type || 'none'
  );
  const [dateOffsetValue, setDateOffsetValue] = useState<number>(
    node?.data.config?.date_config?.offset_value || 0
  );
  const [dateFormat, setDateFormat] = useState<string>(
    node?.data.config?.date_config?.format || 'dd/MM/yyyy'
  );

  /**
   * Preview da data dinâmica calculada
   */
  const datePreview = useMemo(() => {
    if (valueType !== 'dynamic_date') return null;
    return previewDynamicDate({
      base: dateBase,
      offset_type: dateOffsetType,
      offset_value: dateOffsetValue,
      format: dateFormat
    });
  }, [valueType, dateBase, dateOffsetType, dateOffsetValue, dateFormat]);

  /**
   * Sincroniza estados quando node mudar
   */
  useEffect(() => {
    if (node) {
      setVariableId(node.data.config?.variable_id || '');
      setVariableName(node.data.config?.variable_name || '');
      setValueType(node.data.config?.value_type || 'static');
      setValue(node.data.config?.value || '');
      // Sincroniza config de data dinâmica
      if (node.data.config?.date_config) {
        setDateBase(node.data.config.date_config.base || 'today');
        setDateOffsetType(node.data.config.date_config.offset_type || 'none');
        setDateOffsetValue(node.data.config.date_config.offset_value || 0);
        setDateFormat(node.data.config.date_config.format || 'dd/MM/yyyy');
      }
    }
  }, [node]);

  /**
   * Salva a configuração
   */
  const handleSave = () => {
    if (!node) return;

    try {
      // Valida campos básicos
      const validatedData = setVariableSchema.parse({
        variable_id: variableId,
        variable_name: variableName,
        value_type: valueType,
        value: valueType === 'dynamic_date' ? '' : value, // Data dinâmica não usa 'value'
      });

      const config: SetVariableConfig = {
        variable_id: validatedData.variable_id,
        variable_name: validatedData.variable_name,
        value_type: validatedData.value_type,
        value: validatedData.value,
      };

      // Adiciona config de data dinâmica se selecionado
      if (valueType === 'dynamic_date') {
        config.date_config = {
          base: dateBase,
          offset_type: dateOffsetType,
          offset_value: dateOffsetValue,
          format: dateFormat
        };
      }

      onSave(node.id, { ...config, nickname: nicknameRef.current?.getNickname() } as SetVariableConfig);
      toast.success('Configuração salva com sucesso');
      onOpenChange(false);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const firstError = error.errors[0];
        toast.error(firstError.message);
      } else {
        toast.error('Erro ao salvar configuração');
      }
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Configurar Definir Variável</DialogTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowHelp(true)}>
                <HelpCircle className="h-5 w-5" />
              </Button>
            </div>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Campo ID do Node (somente leitura) */}
            <NodeIdField ref={nicknameRef} nodeId={node?.id} defaultNickname={node?.data.config?.nickname} />

            <div className="space-y-2">
              <Label>Variável de Destino *</Label>
              {/* Campo de exibição + botão que abre o modal robusto em modo destino
                  (somente variáveis graváveis do cliente) */}
              <div className="relative w-full">
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  className="w-full justify-between"
                  onClick={() => setShowVarPicker(true)}
                >
                  {variableName ? (
                    <span className="truncate">{variableName}</span>
                  ) : (
                    <span className="text-muted-foreground">
                      Selecione a variável que receberá o valor
                    </span>
                  )}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
                {variableId && (
                  <button
                    type="button"
                    className="absolute right-8 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-destructive z-10"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setVariableId('');
                      setVariableName('');
                    }}
                    aria-label="Limpar seleção"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Variável que será atualizada com o novo valor
              </p>
            </div>

            <div className="space-y-2">
              <Label>Tipo de Valor *</Label>
              <Select value={valueType} onValueChange={(v: any) => setValueType(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="static">Valor Estático</SelectItem>
                  <SelectItem value="variable">Outra Variável</SelectItem>
                  <SelectItem value="expression">Expressão</SelectItem>
                  <SelectItem value="dynamic_date">
                    <span className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Data Dinâmica
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {valueType === 'static' && (
              <div className="space-y-2">
                <Label>Valor Estático *</Label>
                <Input
                  placeholder="Digite o valor fixo"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  maxLength={500}
                />
                <p className="text-xs text-muted-foreground">
                  Valor fixo que será atribuído à variável
                </p>
              </div>
            )}

            {valueType === 'variable' && (
              <div className="space-y-2">
                <Label>Nome da Variável de Origem *</Label>
                <Input
                  placeholder="nome_da_variavel"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  maxLength={100}
                />
                <p className="text-xs text-muted-foreground">
                  Copiar valor de outra variável. Use {'{{nome_variavel}}'} ou apenas nome_variavel
                </p>
              </div>
            )}

            {valueType === 'expression' && (
              <div className="space-y-2">
                <Label>Expressão *</Label>
                <Textarea
                  placeholder={'Exemplo: {{nome}} {{sobrenome}}\nOu: Olá {{nome}}!'}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  rows={4}
                  maxLength={1000}
                />
                <p className="text-xs text-muted-foreground">
                  Combine texto e variáveis. Use {'{{variavel}}'} para inserir valores
                </p>
              </div>
            )}

            {/* Configuração de Data Dinâmica */}
            {valueType === 'dynamic_date' && (
              <div className="space-y-4 p-4 border border-border rounded-lg bg-muted/30">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Calendar className="h-4 w-4 text-primary" />
                  Configuração de Data Dinâmica
                </div>
                
                {/* Data Base */}
                <div className="space-y-2">
                  <Label>Data Base *</Label>
                  <Select value={dateBase} onValueChange={(v: any) => setDateBase(v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="today">Hoje</SelectItem>
                      <SelectItem value="start_of_month">Início do Mês Atual</SelectItem>
                      <SelectItem value="end_of_month">Fim do Mês Atual</SelectItem>
                      <SelectItem value="start_of_year">Início do Ano</SelectItem>
                      <SelectItem value="end_of_year">Fim do Ano</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Ajuste */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Tipo de Ajuste</Label>
                    <Select value={dateOffsetType} onValueChange={(v: any) => setDateOffsetType(v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nenhum</SelectItem>
                        <SelectItem value="days">Dias</SelectItem>
                        <SelectItem value="months">Meses</SelectItem>
                        <SelectItem value="years">Anos</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Valor do Ajuste</Label>
                    <Input
                      type="number"
                      value={dateOffsetValue}
                      onChange={(e) => setDateOffsetValue(parseInt(e.target.value) || 0)}
                      min={-365}
                      max={365}
                      disabled={dateOffsetType === 'none'}
                      placeholder="0"
                    />
                    <p className="text-xs text-muted-foreground">
                      Negativo = passado, Positivo = futuro
                    </p>
                  </div>
                </div>

                {/* Formato */}
                <div className="space-y-2">
                  <Label>Formato de Saída *</Label>
                  <Select value={dateFormat} onValueChange={setDateFormat}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dd/MM/yyyy">dd/MM/yyyy (Brasil)</SelectItem>
                      <SelectItem value="yyyy-MM-dd">yyyy-MM-dd (ISO)</SelectItem>
                      <SelectItem value="MM/dd/yyyy">MM/dd/yyyy (EUA)</SelectItem>
                      <SelectItem value="dd-MM-yyyy">dd-MM-yyyy</SelectItem>
                      <SelectItem value="yyyy/MM/dd">yyyy/MM/dd</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Preview */}
                {datePreview && (
                  <div className="flex items-start gap-2 p-3 bg-primary/10 border border-primary/20 rounded-lg">
                    <Info className="h-4 w-4 text-primary mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-primary">Preview: {datePreview.result}</p>
                      <p className="text-muted-foreground">
                        {datePreview.description} • Formato: {dateFormat}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
              <p className="text-sm text-blue-700 dark:text-blue-400">
                {valueType === 'dynamic_date' ? (
                  <>
                    📅 <strong>Data Dinâmica:</strong> A data será calculada automaticamente no momento 
                    da execução do fluxo. Ideal para APIs que exigem intervalos de data como Hinova.
                  </>
                ) : (
                  <>
                    💡 <strong>Dica:</strong> Use este node para calcular valores, combinar textos, 
                    ou preparar dados antes de enviar mensagens.
                  </>
                )}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave}>
              Salvar Configuração
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal robusto de variáveis em modo destino (somente graváveis) */}
      <VariableExplorerModal
        open={showVarPicker}
        onOpenChange={setShowVarPicker}
        tenantId={currentTenant?.id || ''}
        mode="destination"
        onInsert={() => {}}
        onSelect={(variable) => {
          setVariableId(variable.id);
          setVariableName(variable.name);
        }}
      />

      <NodeHelpDialog 
        open={showHelp} 
        onOpenChange={setShowHelp} 
        nodeType="set_variable" 
      />
    </>
  );
}